import React, { useEffect, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useSelector } from 'react-redux';
import { RootState } from '@store/types';
import { AirflowDashboardHandler } from '@api/airflow-dashboard-handler';
import { errorAlert } from '@components/toastify/notify-toast';
import { DataTable } from '@components/data-table';
import { AirflowAssetEvent } from '../../types';
import { formatDateTime } from '../../utils';
import styles from '../../styles.module.scss';

type TaskEventsTabProps = {
    dagId: string;
    runId: string;
    taskId: string;
    refreshKey: number;
};

/*
 * DAG-run-task detail's Task Events tab ("Asset Events") — asset events produced by this
 * task instance, fixed at a single page of 50
 */
export function TaskEventsTab({ dagId, runId, taskId, refreshKey }: TaskEventsTabProps) {
    const { envVariables: Env } = useSelector((store: RootState) => store.AccountReducer);
    const [events, setEvents] = useState<AirflowAssetEvent[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        AirflowDashboardHandler.listAssetEvents(Env, { sourceDagId: dagId, sourceTaskId: taskId, sourceRunId: runId, limit: 50, offset: 0 })
            .then((response) => {
                if (!cancelled) setEvents(response.asset_events);
            })
            .catch(() => {
                if (!cancelled) errorAlert('Failed to load task events.');
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [Env, dagId, runId, taskId, refreshKey]);

    const columns = useMemo<ColumnDef<AirflowAssetEvent>[]>(
        () => [
            { id: 'timestamp', header: 'Timestamp', enableSorting: false, cell: ({ row }) => formatDateTime(row.original.timestamp) },
            {
                id: 'uri',
                header: 'URI',
                enableSorting: false,
                cell: ({ row }) => <span style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{row.original.dataset_uri ?? row.original.uri ?? '—'}</span>,
            },
            {
                id: 'extra',
                header: 'Extra',
                enableSorting: false,
                cell: ({ row }) => (
                    <div style={{ maxHeight: 54, overflow: 'hidden', fontSize: 12, color: '#666a8d', whiteSpace: 'pre-wrap' }}>
                        {row.original.extra ? JSON.stringify(row.original.extra, null, 2) : '—'}
                    </div>
                ),
            },
        ],
        []
    );

    return (
        <div className={styles.card}>
            <DataTable data={events} columns={columns} isLoading={isLoading} rowKey={(event, index) => `${event.dataset_id ?? index}`} emptyText="No asset events" />
        </div>
    );
}
